import { default as SquareButton } from "@/modules/common/components/ui/square-button";
import { muiTheme } from "@/styles/theme";
import { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import EstimatedCostButton from "../components/finishing-cost-button";
import { format } from "date-fns";
import {
  DropDownConfig,
  FinishingTypes,
  ProposalType,
  HospitalityTypes,
  RentalTags,
  ScopeOfFinishing,
  TrackWithOwner,
} from "../types";
import { UserTag } from "@/modules/user";
import StatusBadge from "@/modules/common/components/ui/status-badge";
import { getPropertyTypesV2 } from "@/modules/common/api/index.v2";
import { fetchCompoundsLookups } from "@/modules/common/api/property-management";
import { formatBoolean } from "@/modules/pools/utils";

export const propertyTrackColumns: ColumnDef<TrackWithOwner>[] = [
  {
    id: "property-track-actions",
    size: 0,
    header: "View Track",
    enableHiding: false,
    cell: ({ row }) => {
      const trackId = row.original.uid;
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Link
            to={`/property-tracks/${trackId}`}
            target="_blank"
            style={{ textDecoration: "none" }}
          >
            <SquareButton
              variant="ghost"
              title="View Property Track"
              sx={{
                color: muiTheme.colorSchemes.light.palette.primary[500],
              }}
            >
              <ExternalLink size={"16px"} />
            </SquareButton>
          </Link>
        </div>
      );
    },
  },
  {
    id: "lead-actions",
    size: 0,
    enableHiding: false,
    header: "View Lead",
    cell: ({ row }) => {
      const journeyUId = row.original.journey?.uid;

      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Link
            to={`/leads-new/${journeyUId}`}
            target="_blank"
            style={{ textDecoration: "none" }}
          >
            <SquareButton
              variant="ghost"
              title="View Lead"
              sx={{
                color: muiTheme.colorSchemes.light.palette.primary[500],
              }}
            >
              <ExternalLink size={"16px"} />
            </SquareButton>
          </Link>
        </div>
      );
    },
  },
  {
    header: "ID",
    id: "uid",
    accessorFn: (row) => row.uid,
  },
  {
    header: "Name",
    id: "name",
    accessorFn: (row) => row.owner?.name,
    // enableSorting: true,
  },
  {
    header: "Telephone",
    id: "phoneNumber",
    accessorFn: (row) => row.owner?.telephone,
  },
  {
    header: "Lead Status",
    id: "leadStatus",
    accessorFn: (row) => row.journey?.status.name,
    enableSorting: true,
  },
  {
    header: "Unit Number",
    id: "unitId",
    accessorFn: (row) => row.property.unitId,
  },
  {
    id: "compoundId",
    header: "Compound",
    accessorFn: (row) => row.property.compound?.name,
    enableSorting: true,
  },
  {
    id: "areaId",
    header: "area",
    accessorFn: (row) => row.property.compound?.area?.name,
    enableSorting: true,
  },
  {
    header: "Type",
    id: "propertyTypeId",
    accessorFn: (row) => row.property.propertyType?.name,
    enableSorting: true,
  },
  {
    id: "trackTypeId",
    header: "Track Type",
    size: 300,
    accessorFn: (row) => row?.trackStage?.trackType.name,
    enableSorting: true,
  },
  {
    id: "stageId",
    header: "Stage",
    accessorFn: (row) => row.trackStage?.name,
    enableSorting: true,
    cell: ({ row }) => {
      if (row.original.trackStage) {
        const { name, color } = row.original.trackStage;
        return <StatusBadge title={name} color={color ?? "#fff"} />;
      }
    },
  },
  {
    id: "statusId",
    header: "Status",
    accessorFn: (row) => row.status.name,
    enableSorting: true,
    cell: ({ row }) => {
      const { name, color } = row.original.status;
      return <StatusBadge title={name} color={color} />;
    },
  },

  {
    id: "updatedAt",
    header: "Updated At",
    size: 550,
    accessorFn: (row) => row?.updatedAt,
    cell: ({ row }) => {
      const updatedAt = row.original?.updatedAt;
      return updatedAt ? format(new Date(updatedAt), "dd/MM/yyyy") : "N/A";
    },
    enableSorting: true,
  },

  {
    id: "createdAt",
    header: "Created At",
    size: 550,
    accessorFn: (row) => row?.createdAt,
    cell: ({ row }) => {
      const createdAt = row.original?.createdAt;
      return createdAt ? format(new Date(createdAt), "dd/MM/yyyy") : "N/A";
    },
    enableSorting: true,
  },

  {
    header: "Sales Agent",
    id: "salesAgentId",
    size: 550,
    accessorFn: (row) => row?.user?.name,
    cell: ({ row }) => {
      const user = row.original?.user;

      if (!user) {
        return null;
      }
      return <UserTag name={user?.name} color={user?.color} />;
    },
    enableSorting: true,
  },
  {
    header: "Sales Agent Phone",
    size: 550,
    accessorFn: (row) => row?.user?.phoneNumber,
  },
  {
    header: "Floor Number",
    id: "floorNumber",
    accessorFn: (row) => row.property.floorNumber,
    enableSorting: true,
  },
  {
    id: "unitArea",
    header: "Unit Area",
    size: 300,
    accessorFn: (row) => row?.property.unitArea,
    cell: ({ row }) => {
      const unitArea = row.original?.property.unitArea;
      return unitArea !== null && unitArea !== undefined
        ? `${unitArea} m²`
        : undefined;
    },
    enableSorting: true,
  },
  {
    header: "Beds",
    id: "numberOfBedrooms",
    accessorFn: (row) => row.property.numberOfBedrooms,
    enableSorting: true,
  },

  {
    header: "Baths",
    id: "numberOfBathrooms",
    accessorFn: (row) => row.property.numberOfBathrooms,
    enableSorting: true,
  },
  {
    header: "Garden Area",
    id: "gardenArea",
    accessorFn: (row) => row.property.gardenArea,
    enableSorting: true,
  },
  {
    header: "Roof Area",
    id: "roofArea",
    accessorFn: (row) => row.property.roofArea,
    enableSorting: true,
  },
  {
    header: "Client Asking Price",
    id: "askingPrice",
    accessorFn: (row) => row.property.propertyPrice?.price?.toLocaleString(),

    enableSorting: true,
  },
  {
    header: "Listing Price",
    id: "price",
    accessorFn: (row) =>
      row.property.propertyListingPrice?.price?.toLocaleString(),

    // enableSorting: true,
  },
  {
    header: "Down Payment",
    id: "downPayment",
    accessorFn: (row) =>
      row.property.propertyListingPrice?.downPayment?.toLocaleString(),
    enableSorting: true,
  },
  {
    header: "Remaining Installments",
    id: "remainingInstallments",
    accessorFn: (row) =>
      row.property.propertyListingPrice?.remainingInstallments?.toLocaleString(),
    enableSorting: true,
  },

  {
    header: "Maintenance",
    id: "maintenance",
    accessorFn: (row) =>
      row.property.propertyListingPrice?.maintenance?.toLocaleString(),
    enableSorting: true,
  },

  {
    header: "Estimated Finishing Cost",
    id: "estimatedFinishingCost",
    cell: ({ row }) => {
      const { unitArea, landArea, numberOfBedrooms } = row.original.property;
      const propertyType = row.original.property.propertyType;
      const compound = row.original.property.compound;

      if (!propertyType || !compound) {
        return "N/A";
      }

      return (
        <EstimatedCostButton
          params={{
            unitArea,
            compoundId: compound?.id,
            propertyTypeId: propertyType?.id,
            landArea,
            numberOfBedrooms,
          }}
          type="Finishing"
        />
      );
    },
  },
  {
    header: "Estimated Rental Income",
    id: "estimatedRentalIncome",
    cell: ({ row }) => {
      const { unitArea, landArea, numberOfBedrooms } = row.original.property;
      const propertyType = row.original.property.propertyType;
      const compound = row.original.property.compound;

      if (!propertyType || !compound) {
        return "N/A";
      }

      return (
        <EstimatedCostButton
          params={{
            unitArea,
            compoundId: compound?.id,
            propertyTypeId: propertyType?.id,
            landArea,
            numberOfBedrooms,
          }}
          type="rental"
        />
      );
    },
  },
  {
    header: "Last Installment Date",
    id: "lastInstallmentDate",
    accessorFn: (row) => row.property.lastInstallmentDate,
    cell: ({ row }) => {
      const lastInstallmentDate = row.original?.property.lastInstallmentDate;
      return lastInstallmentDate
        ? format(new Date(lastInstallmentDate), "dd/MM/yyyy")
        : "N/A";
    },
    enableSorting: true,
  },
  {
    header: "Phase",
    id: "phase",
    accessorFn: (row) => row.property.phase,
  },
  {
    header: "Delivery Date",
    id: "deliveryDate",
    accessorFn: (row) => row.property.deliveryDate,
    cell: ({ row }) => {
      const deliveryDate = row.original?.property.deliveryDate;
      return deliveryDate
        ? format(new Date(deliveryDate), "dd/MM/yyyy")
        : "N/A";
    },
    enableSorting: true,
  },
  {
    header: "Finishing",
    id: "finishing",
    accessorFn: (row) => row.property.finishing,
    cell: ({ row }) => {
      const finishing = row.original?.property.finishing;
      return finishing ? FinishingTypes[finishing] : "N/A";
    },
  },
  {
    header: "Estimated Finishing Date",
    id: "estimatedFinishingDate",
    accessorFn: (row) => row.property.estimatedFinishingDate,
  },
  {
    header: "Under Construction",
    id: "isUnderConstruction",
    accessorFn: (row) => formatBoolean(row.property.isUnderConstruction),
    enableSorting: true,
  },
];

export const StatusFieldsDropDowns: Record<string, DropDownConfig> = {
  propertyTypeId: {
    queryKey: "propertyTypes",
    fetchFn: getPropertyTypesV2,
    transformData: (data: any) =>
      data.data.map((item: any) => ({
        label: item.name,
        value: item.id.toString(),
      })),
    placeholder: "Property Type",
  },
  compoundId: {
    queryKey: "compounds",
    fetchFn: (searchTerm?: string) => fetchCompoundsLookups(searchTerm || ""),
    transformData: (data: any) =>
      data.data.map((item: any) => ({
        label: item.name,
        value: item.id.toString(),
      })),
    placeholder: "Compound",
    searchable: true,
  },
  finishing: {
    placeholder: "Finishing",
    staticOptions: Object.entries(FinishingTypes).map(([key, value]) => ({
      label: value,
      value: key,
    })),
  },
  rentalTag: {
    placeholder: "Rental Type",
    staticOptions: Object.entries(RentalTags).map(([key, value]) => ({
      label: value,
      value: key,
    })),
  },
  hospitalityType: {
    placeholder: "Hospitality Type",
    staticOptions: Object.entries(HospitalityTypes).map(([key, value]) => ({
      label: value,
      value: key,
    })),
  },
  scopeOfFinishing: {
    placeholder: "Scope of Finishing",
    staticOptions: Object.entries(ScopeOfFinishing).map(([key, value]) => ({
      label: value,
      value: key,
    })),
  },
};
export const mainSpecsOptions = {
  Electricity: ["Sewedy Cables, ABB, Schneider Panel"],
  Plumbing: [
    "Kessel / BR Top Tier",
    "Duravit Fixtures (Flushed or Free standing options)",
  ],
  Paint: ["GLC / JOTUN – Washable Top Tier Paint"],
  Flooring: [
    "Imported Marble (Reception/Stairs)",
    "Imported Marble (Tondra Grey)",
    "Imported Turkish Beige Marble",
    "Imported HDF Anti-scratch",
    "Porcelain",
  ],
  "Bathroom Tiles": [
    "Tier I Ceramics (Local Made)",
    "Big Tiles 60x120",
    "Marble (Guest + Master bathroom)",
  ],
  ACs: [
    "Concealed (Reception)",
    "Carrier (Miraco Brands)",
    "Split ACs (Carrier or Midea)",
  ],
  Ceiling: ["Knauf Imported with Tier I Spot Lights & Track Lights"],
  Kitchen: [
    "HPL (colour upon selection)",
    "Fantastic White Granite Counter-top",
    "Double Black Counter-top",
  ],
  Landscape: [
    "Softscape + Hardscape",
    "Automatic Irrigation System",
    "Unit Entrance + Side Planters",
    "Pool",
    "Bar/Kitchenette",
    "Pergola",
  ],
  Doors: ["MDF with Natural Oak Veneer"],
  Roof: ["Pergola", "Built-in seating", "Planters"],
  Elevator: [],
  Elevations: ["Marble Travertine"],
  Shutters: ["All windows", "Main windows"],
};
export const PROPOSAL_TYPE_OPTIONS = [
  { value: ProposalType.Normal, label: "Normal" },
  { value: ProposalType.NawyNow, label: "Nawy Now" },
];

export const proposalIncludesOptions = [
  "Design & Engineering",
  "Finishing",
  "Pool",
  "Elevator",
  "Landscape",
  "Kitchen cabinets",
  "Master Bedroom’s Dressing",
  "Shutter on main Windows",
  "Maintenance & Warranty on works during our contract",
  "Unit Marketing",
  "Property Management",
];
